import React, { Fragment } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Link, styled, Typography, useTheme } from '@mui/material'
import { HorizontalTabs, EidButton, ViewIndicator } from 'packages/eid-ui'
import { useIsSmallScreen } from 'packages/core'
import { useConfiguration } from 'appConfiguration/configurations'
import { useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'
import { DropdownMenu } from 'components/DropdownMenu'
import { SubPathLevel } from '../../appConfiguration'
import appConfig from 'config'
import { ITEMS_VIEW, subHeaderHeight } from 'utils'

const SubHeaderTabs = styled(HorizontalTabs)(({ theme }) => ({
    '& .MuiTabs-flexContainer': {
        height: theme.mixins.navBar.minHeight,
        marginTop: '8px',
        alignItems: 'center',
    },
    '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.primary.main,
        height: '4px',
        padding: '0 6px',
        boxSizing: 'content-box',
        marginLeft: '-6px',
        marginBottom: '0px',
        zIndex: 1,
    },
    '& .MuiTab-textColorInherit.Mui-selected': {
        fontWeight: 'bold',
        letterSpacing: '0.022em',
    },
    '& .MuiTab-root': {
        fontSize: '14px',
        letterSpacing: '0.3px',
        margin: '-2px 0px 0 8px',
        transition: 'color 0.15s ease-in-out',

        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
}))

const StyledNavBarContainer = styled(Box)({
    height: subHeaderHeight,
    marginBottom: '-4px',
    position: 'relative',
    whiteSpace: 'nowrap',

    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        left: 0,
        top: '-4px',
        width: '100%',
        height: 'calc(100% - 2px)',
        borderBottom: '1px solid #d8d8dd',
    },

    '&::after': {
        content: '""',
        display: 'block',
        width: '200%',
        height: '16px',
        backgroundImage:
            'linear-gradient(to top, rgba(238, 240, 244, 0), rgba(238, 240, 244, 0.9))',
        position: 'absolute',
        left: '-50%',
        bottom: '-10px',
    },
})

const SubHeader = (props) => {
    const {
        showCardView,
        toggleCardView,
        toggleFilters,
        showFilters,
        showFiltersButton,
        showBackButtonOnly,
        showBackButton,
        customTabs = [],
        onTabChange,
    } = props

    const theme = useTheme()
    const { t } = useTranslation()
    const { dropDownOptions } = useConfiguration()
    const isSmallScreen = useIsSmallScreen()
    const history = useHistory()

    const {
        location: { pathname },
    } = history
    const pathnames = pathname.split('/')
    const selectedOption = dropDownOptions.find(
        (o) =>
            o.route ===
            `${appConfig.APP_SUBPATH}/${pathnames[1 + SubPathLevel]}`,
    )
    let activeTab = history.location.pathname
    return (
        <StyledNavBarContainer
            component="nav"
            aria-label="Subheader navigation"
            height={isSmallScreen ? '128px' : subHeaderHeight}
            display="flex"
            flexDirection={isSmallScreen ? 'column' : 'row'}
            alignItems={isSmallScreen ? 'start' : 'center'}
            justifyContent="space-between"
            width="100%"
            data-ai-explain="SubHeader: navigation tabs and view options"
        >
            {showBackButtonOnly ? (
                <Box
                    width="100%"
                    display="flex"
                    justifyContent="flex-end"
                    data-ai-explain="Back button"
                >
                    <EidButton.BackButton label={t('Back')} />
                </Box>
            ) : (
                <Fragment>
                    <Box data-ai-explain="Main navigation dropdown">
                        <DropdownMenu
                            routeOptions={dropDownOptions}
                            totalCount={5}
                        />
                    </Box>

                    {!isSmallScreen &&
                        selectedOption?.tabbedOptions?.length > 0 && (
                            <SubHeaderTabs
                                variant="scrollable"
                                value={activeTab}
                                onChange={(_, value) => {
                                    history.push(value)
                                    if (onTabChange) {
                                        onTabChange()
                                    }
                                }}
                                data-ai-explain="Page tabs"
                            >
                                {selectedOption.tabbedOptions.map((op) => (
                                    <HorizontalTabs.Tab
                                        tabIndex={0}
                                        key={op.name}
                                        value={`${
                                            selectedOption.route + op.route
                                        }`}
                                        label={t(op.title)}
                                        data-ai-explain={`Tab: ${t(op.title)}`}
                                    />
                                ))}
                                {customTabs.map((op) => (
                                    <HorizontalTabs.Tab
                                        tabIndex={0}
                                        key={op.name}
                                        value={`${
                                            selectedOption.route + op.route
                                        }`}
                                        label={t(op.title)}
                                        data-ai-explain={`Tab: ${t(op.title)}`}
                                    />
                                ))}

                                {selectedOption.AddNewTabOption &&
                                    selectedOption.AddNewTabOption.render()}
                            </SubHeaderTabs>
                        )}

                    {isSmallScreen && showFiltersButton && (
                        <Link
                            component="button"
                            onClick={toggleFilters}
                            aria-label={
                                showFilters ? 'Hide filters' : 'Show filters'
                            }
                            style={{ marginRight: '20px', minWidth: '24px' }}
                            data-ai-explain={showFilters ? 'Hide filters' : 'Show filters'}
                        >
                            <Icon
                                name="AdvancedSearch"
                                color={
                                    showFilters
                                        ? theme.palette.primary.main
                                        : '#959598'
                                }
                            />
                        </Link>
                    )}
                    {!isSmallScreen && toggleCardView && (
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            data-ai-explain="Filters and view toggle"
                        >
                            {showFiltersButton && (
                                <Link
                                    component="button"
                                    onClick={toggleFilters}
                                    aria-label={
                                        showFilters
                                            ? 'Hide filters'
                                            : 'Show filters'
                                    }
                                    style={{
                                        marginRight: '20px',
                                        minWidth: '24px',
                                    }}
                                    data-ai-explain={showFilters ? 'Hide filters' : 'Show filters'}
                                >
                                    <Icon
                                        name="AdvancedSearch"
                                        color={
                                            showFilters
                                                ? theme.palette.primary.main
                                                : '#959598'
                                        }
                                    />
                                </Link>
                            )}

                            <Box
                                component="span"
                                data-ai-explain="Card view / List view toggle"
                            >
                                <ViewIndicator
                                    view={
                                        showCardView
                                            ? ITEMS_VIEW.CARD
                                            : ITEMS_VIEW.LIST
                                    }
                                    onCardViewClick={() => toggleCardView(true)}
                                    onListViewClick={() => toggleCardView(false)}
                                    cardViewLabel={t('Card_View_Label')}
                                    listViewLabel={t('List_View_Label')}
                                />
                            </Box>
                        </Box>
                    )}

                    {showBackButton && (
                        <Box
                            component="span"
                            data-ai-explain="Back to workflows"
                        >
                            <EidButton.BackButton label={t('BackToWorkflows')} />
                        </Box>
                    )}

                    {!showFiltersButton &&
                        !toggleCardView &&
                        !showBackButton && <Box></Box>}
                </Fragment>
            )}
        </StyledNavBarContainer>
    )
}

export default SubHeader
